import { useState, useEffect } from 'react';
import LOCATIONS from '../constants/locations';
import Modal from './ui/Modal';
import Input from './ui/Input';
import Button from './ui/Button';
import MapAddressPicker from './MapAddressPicker';

const emptyForm = {
  label: '',
  location: '',
  address_line: '',
  building: '',
  unit: '',
  latitude: null,
  longitude: null,
};

export default function AddressFormModal({
  isOpen,
  onClose,
  address,
  forceDefault = false,
  createAddress,
  updateAddress,
  onSaved,
}) {
  const [form, setForm] = useState(emptyForm);
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    if (address) {
      setForm({
        label: address.label || '',
        location: address.location || '',
        address_line: address.address_line || '',
        building: address.building || '',
        unit: address.unit || '',
        latitude: address.latitude ?? null,
        longitude: address.longitude ?? null,
      });
      setIsDefault(address.is_default);
    } else {
      setForm(emptyForm);
      setIsDefault(forceDefault);
    }
    setError(null);
  }, [isOpen, address, forceDefault]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const handleMapChange = (coords) => {
    setForm((f) => ({ ...f, ...coords, address_line: '' }));
  };

  const handleResolvedAddress = (resolved) => {
    setForm((f) => ({
      ...f,
      address_line: resolved.address_line || f.address_line,
      building: f.building || resolved.building,
      location: resolved.location,
    }));
  };

  const handleSave = async () => {
    if (!form.location) {
      setError('Select an emirate.');
      return;
    }
    if (!form.address_line.trim()) {
      setError('Address line is required.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = { ...form, is_default: isDefault };
      const saved = address
        ? await updateAddress(address.id, payload)
        : await createAddress(payload);
      onSaved?.(saved);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !saving && onClose()}
      title={address ? 'Edit Address' : 'Add Address'}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" loading={saving} onClick={handleSave}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Label (optional)"
          id="address-label"
          value={form.label}
          onChange={handleChange('label')}
          placeholder="Home, Work…"
          disabled={saving}
        />

        <MapAddressPicker
          value={form.latitude != null ? { latitude: form.latitude, longitude: form.longitude } : null}
          onChange={handleMapChange}
          onResolvedAddress={handleResolvedAddress}
        />

        <div className="flex flex-col gap-1">
          <label htmlFor="address-location" className="text-sm font-medium text-ink-700">
            Emirate
          </label>
          <select
            id="address-location"
            value={form.location}
            onChange={handleChange('location')}
            disabled
            className="w-full px-4 py-2.5 rounded-md border border-sand-200 bg-sand-50 text-ink-900 text-sm cursor-not-allowed"
          >
            <option value="">Select emirate</option>
            {LOCATIONS.map((loc) => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
          
        </div>

        <Input
          label="Address line"
          id="address-line"
          value={form.address_line}
          onChange={handleChange('address_line')}
          placeholder="Street / area name"
          disabled={saving}
        />

        <div className="flex gap-3">
          <Input
            label="Building"
            id="address-building"
            value={form.building}
            onChange={handleChange('building')}
            disabled={saving}
            className="flex-1"
          />
          <Input
            label="Unit / Flat"
            id="address-unit"
            value={form.unit}
            onChange={handleChange('unit')}
            disabled={saving}
            className="flex-1"
          />
        </div>

        {!forceDefault && (
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              disabled={saving || address?.is_default}
              className="rounded border-sand-300 text-terra-400 focus:ring-terra-400"
            />
            Set as default address
          </label>
        )}

        {error && <p className="text-xs text-uae-red">{error}</p>}
      </div>
    </Modal>
  );
}
